import React from "react";
import { useQueryClient } from "@tanstack/react-query";
import { toast } from "@kkp/ui";
import {
  bulkUpdateCompanyStatus,
  exportCompanies,
  listCompanies,
  updateCompanyStatus,
  type Company,
} from "../../../api/companies";

const INITIAL_COMPANIES: Company[] = [];

/**
 * Owns the admin Clients company list: the initial load plus the mutations that
 * touch it (status toggle, bulk status, CSV export) with their API calls +
 * toasts. Kept per-app (not shared in @kkp/features): the admin flow invalidates
 * the company-detail query on a status toggle, which the client flow does not.
 */
export function useClientsData() {
  const [companies, setCompanies] =
    React.useState<Company[]>(INITIAL_COMPANIES);
  const [isLoading, setIsLoading] = React.useState(true);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    let mounted = true;
    listCompanies()
      .then((data) => {
        if (!mounted) return;
        setCompanies(
          [...data].sort((a, b) => b.updatedAt.localeCompare(a.updatedAt)),
        );
      })
      .catch((err) => {
        if (!mounted) return;
        console.error(err);
        toast.danger("Failed to load companies.", {
          title: "Error",
          durationMs: 2600,
        });
      })
      .finally(() => {
        if (!mounted) return;
        setIsLoading(false);
      });
    return () => {
      mounted = false;
    };
  }, []);

  const appendCompany = (company: Company) => {
    setCompanies((prev) => [company, ...prev]);
  };

  const applyUpdate = (updated: Company) => {
    setCompanies((prev) =>
      prev.map((c) => (c.id === updated.id ? { ...c, ...updated } : c)),
    );
  };

  const toggleStatus = (c: Company) => {
    const nextStatus = c.status === "Active" ? "Inactive" : "Active";
    updateCompanyStatus(c.id, nextStatus)
      .then((updated) => {
        setCompanies((prev) =>
          prev.map((item) =>
            item.id === updated.id ? { ...item, ...updated } : item,
          ),
        );
        queryClient.invalidateQueries({ queryKey: ["company-detail", c.id] });
        toast.success(
          nextStatus === "Active"
            ? "Company activated."
            : "Company deactivated.",
          {
            title: c.name,
            durationMs: 2000,
          },
        );
      })
      .catch(() => {
        toast.danger("Failed to update status.", { title: c.name });
      });
  };

  const bulkSetStatus = (ids: string[], status: "Active" | "Inactive") => {
    bulkUpdateCompanyStatus(ids, status)
      .then(() => {
        setCompanies((prev) =>
          prev.map((c) =>
            ids.includes(c.id) ? { ...c, status, updatedAt: c.updatedAt } : c,
          ),
        );
        toast.success("Updated selected companies.", {
          title: "Bulk actions",
          durationMs: 2400,
        });
      })
      .catch(() => {
        toast.danger("Bulk update failed.", { title: "Bulk actions" });
      });
  };

  const exportSelected = (ids: string[]) => {
    exportCompanies(ids)
      .then((res) => {
        toast.success("Export ready.", {
          title: "Bulk actions",
          durationMs: 2400,
        });
        if (res?.url) {
          window.open(res.url, "_blank", "noopener,noreferrer");
        }
      })
      .catch(() => {
        toast.danger("Export failed.", { title: "Bulk actions" });
      });
  };

  return {
    companies,
    isLoading,
    appendCompany,
    applyUpdate,
    toggleStatus,
    bulkSetStatus,
    exportSelected,
  };
}
